import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { STORAGE_KEYS } from '../../constants/storageKeys'
import { availabilitiesThunk, checkDocStatus, editCompletedPersonalDetail, getContractForm, getFormFieldsThunk, getProfileDetailsThunk, getSuperFormFieldsThunk, interviewCompleteLaterThunk, jobPreferencesThunk, myProfileStageSubmit, personalDetailsThunk, profileEditRequestThunk, qualificationsThunk, saveBankAccountThunk, saveEmergencyInfoThunk, sendConnectIdDetailsThunk, startInterviewThunk, startReferroChecksThunk, submitTFNForm } from './thunk'
import { BankAccErrorPayload, IOnboardingState, TAvailabilities, TBankAccount, TBankValidationErrors, TCandidateProfileStesps, TCompletionStatus, TEmergencyInfo, TFNErrorPayload, TGetFormFieldsResponse, TJobPreference, TPersonalDetail, TQualification, TUserData } from './types'

/**
 * Clear sessionStorage while preserving impersonation data
 * This prevents impersonation sessions from being interrupted by onboarding actions
 */
const clearSessionStoragePreserveImpersonation = () => {
  const impersonationData = sessionStorage.getItem(STORAGE_KEYS.IMPERSONATION_DATA)
  sessionStorage.clear()
  if (impersonationData) {
    sessionStorage.setItem(STORAGE_KEYS.IMPERSONATION_DATA, impersonationData)
  }
}

const initialState: IOnboardingState = {
  inProgress: false,
  submitBtnInProgress: false,
  error: '',
  userpersonaldetails: null,
  personaldetails: null,
  qualifications: null,
  jobpreferences: null,
  completedstatus: null,
  isCompleteLater: false,
  showEnterPortal: false,
  tfnForm: null,
  flkFormData: '',
  docStatus: '',
  superForm: null,
  viddiMailStatus: false,
  viddiStatus: {
    viddi_candidate_interview_status: 0,
    viddi_mail_status: false,
    viddi_response: null,
    viddi_review_status: 0,
    viddi_status: 0,
    viddi_authless_login_url: null
  },
  viddiReviewStatus: 0,
  isConnectIdVerified: false,
  emergencyinfodetails: null,
  availabilities: null,
  referralCode: '',
  candidateProfileSteps: [],
  myProfileStageSubmitProgress: false,
  contract_form_url: '',
  contract_form_signed_url: '',
  bankAccount: null,
  qualificationMissingDocs: 0,
  errorMessage: '',
  bankAccErrors: {},
  interviewCompleteLaterInProgress: false,
  referro_data: {
    referoo_response: null,
    dashboard_url: null,
    detail_url: null,
    read_only_url: null,
  },
  reopenedStagesStatus: null
}

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    resetOnboardingErrorAction: (state) => {
      state.error = null
    },
    setCompleteLaterAction: (state, action: PayloadAction<boolean>) => {
      state.isCompleteLater = action.payload
    },
    setUserPersonalDetailsAction: (state, action: PayloadAction<TUserData>) => {
      state.userpersonaldetails = action.payload
    },
    setPersonalDetailsAction: (state, action: PayloadAction<TPersonalDetail>) => {
      state.personaldetails = action.payload
    },
    setQualificationsAction: (state, action: PayloadAction<TQualification>) => {
      state.qualifications = action.payload
    },
    setJobPreferencesAction: (state, action: PayloadAction<TJobPreference[]>) => {
      const jobpreferences = action.payload
      state.jobpreferences = jobpreferences[0]
    },
    setCompletionStatusAction: (state, action: PayloadAction<TCompletionStatus>) => {
      state.completedstatus = action.payload
    },
    setTFNFormAction: (state, action: PayloadAction<TGetFormFieldsResponse>) => {
      state.tfnForm = action.payload
    },
    setShowEnterPortalAction: (state, action: PayloadAction<boolean>) => {
      state.showEnterPortal = action.payload
    },
    setFlkFormData: (state, action: PayloadAction<any>) => {
      state.flkFormData = action.payload
    },
    setDocStatus: (state, action: PayloadAction<any>) => {
      state.docStatus = action.payload
    },
    setSuperFormAction: (state, action: PayloadAction<any>) => {
      state.superForm = action.payload
    },
    clearTfnForm: (state) => {
      state.tfnForm = null
    },
    setViddiMailStatusAction: (state, action: PayloadAction<boolean>) => {
      state.viddiMailStatus = action.payload
    },
    setViddiStatusAction: (state, action: PayloadAction<{
      viddi_candidate_interview_status: number;
      viddi_mail_status: boolean;
      viddi_response: string | null;
      viddi_review_status: number;
      viddi_status: number;
      viddi_authless_login_url: string | null;
    }>) => {
      state.viddiStatus = action.payload
    },
    setViddiReviewStatusAction: (state, action: PayloadAction<number>) => {
      state.viddiReviewStatus = action.payload
    },
    setIsConnectIdVerifiedAction: (state, action: PayloadAction<boolean>) => {
      state.isConnectIdVerified = action.payload
    },
    setNextOfKinAction: (state, action: PayloadAction<TEmergencyInfo>) => {
      state.emergencyinfodetails = action.payload
    },
    setAvailabilityAction: (state, action: PayloadAction<TAvailabilities>) => {
      state.availabilities = action.payload
    },
    setReferralCodeAction: (state, action: PayloadAction<string>) => {
      state.referralCode = action.payload
    },
    setProfileStepsAction: (state, action: PayloadAction<TCandidateProfileStesps[]>) => {
      state.candidateProfileSteps = action.payload
    },
    setContractFormAction: (state, action: PayloadAction<any>) => {
      state.contract_form_url = action.payload
    },
    setContractSignedFormUrl: (state, action: PayloadAction<string>) => {
      state.contract_form_signed_url = action.payload
    },
    setBankAccountsDetailAction: (state, action: PayloadAction<TBankAccount>) => {
      state.bankAccount = action.payload
    },
    setMissingDocsCountAction: (state, action: PayloadAction<number>) => {
      state.qualificationMissingDocs = action.payload
    },
    setTfnSuperformErrorMsgAction: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload
    },
    setBankAccErrorMsgAction: (state, action: PayloadAction<TBankValidationErrors>) => {
      state.bankAccErrors = action.payload
    },
    setSubmitBtnLoaderAction: (state, action: PayloadAction<boolean>) => {
      state.submitBtnInProgress = action.payload;
    },
    setReopenedStagesStatusAction: (state, action: PayloadAction<any>) => {
      state.reopenedStagesStatus = action.payload;
    }
  },
  // Async actions
  extraReducers: (builder) => {
    // personal-details
    builder.addCase(personalDetailsThunk.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(personalDetailsThunk.fulfilled, (state, action) => {
      state.inProgress = false
      clearSessionStoragePreserveImpersonation()
    })
    builder.addCase(personalDetailsThunk.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload
    })

    // qualification
    builder.addCase(qualificationsThunk.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(qualificationsThunk.fulfilled, (state, action) => {
      state.inProgress = false
      clearSessionStoragePreserveImpersonation()
    })
    builder.addCase(qualificationsThunk.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload
    })

    // job-preferences
    builder.addCase(jobPreferencesThunk.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(jobPreferencesThunk.fulfilled, (state, action) => {
      state.inProgress = false
      clearSessionStoragePreserveImpersonation()
    })
    builder.addCase(jobPreferencesThunk.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload
    })

    // availabilities
    builder.addCase(availabilitiesThunk.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(availabilitiesThunk.fulfilled, (state, action) => {
      state.inProgress = false
    })
    builder.addCase(availabilitiesThunk.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload
    })

    // getUserDetails
    builder.addCase(getProfileDetailsThunk.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(getProfileDetailsThunk.fulfilled, (state, action) => {
      state.inProgress = false
    })
    builder.addCase(getProfileDetailsThunk.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload
    })

    // complaince check
    builder.addCase(startReferroChecksThunk.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(startReferroChecksThunk.fulfilled, (state, action) => {
      state.inProgress = false
    })
    builder.addCase(startReferroChecksThunk.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload
    })

    // getTFNFormFields
    builder.addCase(getFormFieldsThunk.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(getFormFieldsThunk.fulfilled, (state, action) => {
      state.inProgress = false
    })
    builder.addCase(getFormFieldsThunk.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload
    })

    // postTFNFormFields
    builder.addCase(submitTFNForm.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(submitTFNForm.fulfilled, (state, action) => {
      state.inProgress = false
    })
    builder.addCase(submitTFNForm.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload

      const errorPayload = action.payload as TFNErrorPayload;
      let msg = "";
      if(errorPayload?.data?.error_details?.api_response?.message) {
        msg = errorPayload?.data?.error_details?.api_response?.message;
      } else if(errorPayload?.message) {
        msg = errorPayload?.message;
      } else {
        msg = 'Could not connect to services at the moment. Please try again. Thanks.';
      }
      state.errorMessage = msg;
    })

    // postCheckDocStatus
    builder.addCase(checkDocStatus.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(checkDocStatus.fulfilled, (state, action) => {
      state.inProgress = false
    })
    builder.addCase(checkDocStatus.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload
    })

    // getSuperFormFields
    builder.addCase(getSuperFormFieldsThunk.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(getSuperFormFieldsThunk.fulfilled, (state, action) => {
      state.inProgress = false
    })
    builder.addCase(getSuperFormFieldsThunk.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload
    })

    // submit the Next of Kin form
    builder.addCase(saveEmergencyInfoThunk.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(saveEmergencyInfoThunk.fulfilled, (state, action) => {
      state.inProgress = false
      clearSessionStoragePreserveImpersonation()
    })
    builder.addCase(saveEmergencyInfoThunk.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload
    })

    // click to start interview
    builder.addCase(startInterviewThunk.pending, (state, action) => {
      state.submitBtnInProgress = true
      state.error = null
    })
    builder.addCase(startInterviewThunk.fulfilled, (state, action) => {
      state.submitBtnInProgress = false
    })
    builder.addCase(startInterviewThunk.rejected, (state, action) => {
      state.submitBtnInProgress = false
      state.error = action.payload
    })
    
    // send connect id datas to backend
    builder.addCase(sendConnectIdDetailsThunk.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(sendConnectIdDetailsThunk.fulfilled, (state, action) => {
      state.inProgress = false
    })
    builder.addCase(sendConnectIdDetailsThunk.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload
    })

    builder.addCase(editCompletedPersonalDetail.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(editCompletedPersonalDetail.fulfilled, (state, action) => {
      state.inProgress = false
    })
    builder.addCase(editCompletedPersonalDetail.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload
    })

    builder.addCase(profileEditRequestThunk.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(profileEditRequestThunk.fulfilled, (state, action) => {
      state.inProgress = false
    })
    builder.addCase(profileEditRequestThunk.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload
    })

    builder.addCase(interviewCompleteLaterThunk.pending, (state, action) => {
      state.interviewCompleteLaterInProgress = true
      state.error = null
    })
    builder.addCase(interviewCompleteLaterThunk.fulfilled, (state, action) => {
      state.interviewCompleteLaterInProgress = false
    })
    builder.addCase(interviewCompleteLaterThunk.rejected, (state, action) => {
      state.interviewCompleteLaterInProgress = false
      state.error = action.payload
    })

    builder.addCase(myProfileStageSubmit.pending, (state, action) => {
      state.myProfileStageSubmitProgress = true
      state.error = null
    })
    builder.addCase(myProfileStageSubmit.fulfilled, (state, action) => {
      state.myProfileStageSubmitProgress = false
    })
    builder.addCase(myProfileStageSubmit.rejected, (state, action) => {
      state.myProfileStageSubmitProgress = false
      state.error = action.payload
    })

    builder.addCase(getContractForm.pending, (state, action) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(getContractForm.fulfilled, (state, action) => {
      state.inProgress = false
    })
    builder.addCase(getContractForm.rejected, (state, action) => {
      state.inProgress = false
      state.error = action.payload
    })

    builder.addCase(saveBankAccountThunk.pending, (state) => {
      state.inProgress = true
      state.error = null
    })
    builder.addCase(saveBankAccountThunk.fulfilled, (state) => {
      state.inProgress = false
    })
    builder.addCase(saveBankAccountThunk.rejected, (state, action) => {
      state.inProgress = false
      // state.error = action.payload

      const errorPayload = action.payload as BankAccErrorPayload;
      state.error = errorPayload;

      state.bankAccErrors = errorPayload.errors as TBankValidationErrors ?? {};
    })
    
  }
})

export const { 
  setCompleteLaterAction,
  setUserPersonalDetailsAction,
  setPersonalDetailsAction,
  setQualificationsAction,
  setJobPreferencesAction,
  setCompletionStatusAction,
  setTFNFormAction,
  setShowEnterPortalAction,
  resetOnboardingErrorAction,
  setFlkFormData,
  setDocStatus,
  setSuperFormAction,
  clearTfnForm,
  setViddiMailStatusAction,
  setViddiStatusAction,
  setViddiReviewStatusAction,
  setIsConnectIdVerifiedAction,
  setNextOfKinAction,
  setAvailabilityAction,
  setReferralCodeAction,
  setProfileStepsAction,
  setContractFormAction,
  setContractSignedFormUrl,
  setBankAccountsDetailAction,
  setMissingDocsCountAction,
  setTfnSuperformErrorMsgAction,
  setBankAccErrorMsgAction,
  setSubmitBtnLoaderAction,
  setReopenedStagesStatusAction
} = onboardingSlice.actions

export default onboardingSlice.reducer
